<template>
  <div class="call-result-type-select-container">
    <BSelect
      :model-value="selectValues"
      multiple
      collapse-tags
      collapse-tags-tooltip
      :placeholder="i18n.t('callResult.allCallResultTypes')"
      :items="items"
      item-text="name"
      item-value="value"
      fit
      :loading="loading"
      @update:model-value="handleUpdateModelValue"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, defineModel } from 'vue';
import { useI18n } from 'vue-i18n';
import { CustomCallResultTypeSummary, DefaultCallResultTypeSummary } from '@/api/openapi';
import { CallResultTypeApiService } from '@/api/user/resources/call_result_type';
import { useWait } from '@/composable/vue-wait';

type TModelValueType = 'custom' | 'default';

const i18n = useI18n();

const CUSTOM_PREFIX = 'custom_';
const DEFAULT_PREFIX = 'default_';

const defaultCallResultTypeIds = defineModel<number[]>('defaultCallResultTypeIds', { default: () => [] });
const customCallResultTypeIds = defineModel<number[]>('customCallResultTypeIds', { default: () => [] });

const customCallResultTypes = ref<CustomCallResultTypeSummary[]>([]);
const defaultCallResultTypes = ref<DefaultCallResultTypeSummary[]>([]);

const { doActionWithWait, wait } = useWait();

const loading = computed(() => {
  return wait.is('fetchCallResultTypeSummaries');
});

const items = computed(() => {
  return [
    ...defaultCallResultTypes.value.map((defaultCallResultType) => ({
      value: buildModelValue(defaultCallResultType.id, 'default'),
      name: defaultCallResultType.name,
    })),
    ...customCallResultTypes.value.map((customCallResultType) => ({
      value: buildModelValue(customCallResultType.id, 'custom'),
      name: customCallResultType.name,
    })),
  ];
});

const selectValues = computed<string[]>(() => {
  return [
    ...customCallResultTypeIds.value.map((id) => buildModelValue(id, 'custom')),
    ...defaultCallResultTypeIds.value.map((id) => buildModelValue(id, 'default')),
  ];
});

const fetchCallResultTypeSummaries = async () => {
  await doActionWithWait('fetchCallResultTypeSummaries', async () => {
    const api = new CallResultTypeApiService();
    const response = await api.getCallResultTypeSummaries();
    customCallResultTypes.value = response.data.customCallResultTypes;
    defaultCallResultTypes.value = response.data.defaultCallResultTypes;
  });
};

const buildModelValue = (value: number, type: TModelValueType) => {
  if (value == null) return null;
  if (type === 'custom') {
    return `${CUSTOM_PREFIX}${value}`;
  } else if (type === 'default') {
    return `${DEFAULT_PREFIX}${value}`;
  }
  return null;
};

const handleUpdateModelValue = (values: string[]) => {
  const newDefaultCallResultTypeIds = values.filter((v) => v.startsWith(DEFAULT_PREFIX)).map((v) => Number(v.substring(DEFAULT_PREFIX.length)));
  const newCustomCallResultTypeIds = values.filter((v) => v.startsWith(CUSTOM_PREFIX)).map((v) => Number(v.substring(CUSTOM_PREFIX.length)));
  defaultCallResultTypeIds.value = newDefaultCallResultTypeIds;
  customCallResultTypeIds.value = newCustomCallResultTypeIds;
};

onMounted(() => {
  fetchCallResultTypeSummaries();
});
</script>

<style lang="scss" scoped>
.call-result-type-select-container {
  width: 100%;
}
</style>
